import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { type IfrsReportCandidateDto, useGetFilteredIfrsReportPreviewQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { ListLoadError } from "../../../common/ListLoadError";
import { downloadChosenIfrsReport } from "../ifrsReportDownload";
import { rememberedUnticked, rememberUnticked } from "./ifrsReportSelection";

export const defaultIfrsPeriod = (now: Date) => ({
  from: `${now.getFullYear()}-01`,
  to: `${now.getFullYear()}-12`,
});

export const ifrsTerm = (candidate: Pick<IfrsReportCandidateDto, "ifrsStart" | "ifrsEnd">): string =>
  `${monthYear(candidate.ifrsStart)}–${monthYear(candidate.ifrsEnd)}`;

const monthYear = (yearMonth: string): string => {
  const [year, month] = yearMonth.split("-");
  return `${month}.${year}`;
};

const isMonth = (value: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

export const MAX_PERIOD_YEARS = 100;

const isLongerThanAllowed = (from: string, to: string) => {
  const [fromYear, fromMonth] = from.split("-");
  return `${Number(fromYear) + MAX_PERIOD_YEARS}-${fromMonth}` <= to;
};

export const IfrsReportDialog = ({ encodedQ, onClose }: { encodedQ: string; onClose: () => void }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const [period, setPeriod] = useState(() => defaultIfrsPeriod(new Date()));
  const [party, setParty] = useState("");
  const [search, setSearch] = useState("");
  const [unticked, setUnticked] = useState<Set<string>>(() => rememberedUnticked(selectedOrg.id));
  const [isDownloading, setIsDownloading] = useState(false);

  const periodIsComplete = isMonth(period.from) && isMonth(period.to);
  const periodIsReversed = periodIsComplete && period.from > period.to;
  const periodIsTooLong = periodIsComplete && !periodIsReversed && isLongerThanAllowed(period.from, period.to);
  const periodIsValid = periodIsComplete && !periodIsReversed && !periodIsTooLong;
  const preview = useGetFilteredIfrsReportPreviewQuery(
    { q: encodedQ, periodFrom: period.from, periodTo: period.to },
    { skip: !periodIsValid, refetchOnMountOrArgChange: true },
  );
  const candidates = useMemo(() => preview.currentData?.candidates ?? [], [preview.currentData]);
  const parties = useMemo(
    () =>
      [...new Map(candidates.flatMap((c) => c.internalParties).map((p) => [p.id, p.name])).entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [candidates],
  );
  const chosenParty = parties.some((p) => p.id === party) ? party : "";
  const shown = candidates.filter(
    (c) =>
      (chosenParty === "" || c.internalParties.some((p) => p.id === chosenParty)) &&
      c.title.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const chosen = shown.filter((c) => !unticked.has(c.id));
  const allShownTicked = shown.length > 0 && chosen.length === shown.length;

  const setTicked = (ids: string[], ticked: boolean) =>
    setUnticked((current) => {
      const next = new Set(current);
      ids.forEach((id) => (ticked ? next.delete(id) : next.add(id)));
      return next;
    });

  const download = async () => {
    setIsDownloading(true);
    try {
      await downloadChosenIfrsReport(
        chosen.map((c) => c.id),
        period,
      );
      rememberUnticked(selectedOrg.id, candidates.map((c) => c.id).filter((id) => unticked.has(id)));
      onClose();
    } catch {
      toast({ description: t("The report could not be downloaded. Try again."), status: "error" });
    } finally {
      setIsDownloading(false);
    }
  };

  const blocked = !periodIsValid || chosen.length === 0 || preview.isFetching;

  return (
    <Modal isOpen onClose={onClose} size="2xl" closeOnOverlayClick={!isDownloading} closeOnEsc={!isDownloading}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("IFRS 16 report")}</ModalHeader>
        <ModalCloseButton isDisabled={isDownloading} />
        <ModalBody>
          <Stack spacing="4">
            <Stack spacing="1">
              <Text fontWeight="semibold" fontSize="sm">
                {t("Period")}
              </Text>
              <Flex gap="3" flexWrap="wrap">
                <FormControl width="auto">
                  <FormLabel fontSize="sm" mb="1">
                    {t("From")}
                  </FormLabel>
                  <Input
                    type="month"
                    size="sm"
                    value={period.from}
                    onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                  />
                </FormControl>
                <FormControl width="auto">
                  <FormLabel fontSize="sm" mb="1">
                    {t("To")}
                  </FormLabel>
                  <Input
                    type="month"
                    size="sm"
                    value={period.to}
                    onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                  />
                </FormControl>
              </Flex>
              {!periodIsComplete && (
                <Text fontSize="sm" color="smError">
                  {t("Choose both a from month and a to month.")}
                </Text>
              )}
              {periodIsReversed && (
                <Text fontSize="sm" color="smError">
                  {t("The from month cannot be after the to month.")}
                </Text>
              )}
              {periodIsTooLong && (
                <Text fontSize="sm" color="smError">
                  {t("The period cannot be longer than {{years}} years.", { years: MAX_PERIOD_YEARS })}
                </Text>
              )}
            </Stack>
            <Flex gap="3" flexWrap="wrap" align="flex-end">
              <FormControl width={{ base: "full", md: "auto" }} minW="56">
                <FormLabel fontSize="sm" mb="1">
                  {t("Internal party")}
                </FormLabel>
                <Select size="sm" value={chosenParty} onChange={(e) => setParty(e.target.value)}>
                  <option value="">{t("All internal parties")}</option>
                  {parties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <InputGroup size="sm" flex="1" minW="48">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="smMuted" />
                </InputLeftElement>
                <Input
                  placeholder={t("Search by title") ?? ""}
                  aria-label={t("Search by title") ?? ""}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Flex>
            {preview.isFetching ? (
              <Flex justify="center" py="6">
                <Spinner />
              </Flex>
            ) : preview.isError ? (
              <ListLoadError
                message={t("Could not load contracts.")}
                onRetry={() => preview.refetch()}
                isRetrying={preview.isFetching}
              />
            ) : (
              periodIsValid && (
                <Stack spacing="2">
                  <Checkbox
                    isChecked={allShownTicked}
                    isIndeterminate={chosen.length > 0 && !allShownTicked}
                    isDisabled={shown.length === 0}
                    onChange={(e) =>
                      setTicked(
                        shown.map((c) => c.id),
                        e.target.checked,
                      )
                    }
                  >
                    {t("Select all ({{selected}} of {{total}} selected)", {
                      selected: chosen.length,
                      total: shown.length,
                    })}
                  </Checkbox>
                  <Divider />
                  <Box maxH="72" overflowY="auto">
                    <Stack spacing="1">
                      {shown.length === 0 && (
                        <Text fontSize="sm" color="smMuted" py="2">
                          {candidates.length === 0
                            ? t("No contracts have months in the period.")
                            : t("No contracts match the search or filter.")}
                        </Text>
                      )}
                      {shown.map((c) => (
                        <Checkbox
                          key={c.id}
                          isChecked={!unticked.has(c.id)}
                          onChange={(e) => setTicked([c.id], e.target.checked)}
                        >
                          <Flex gap="3" fontSize="sm" flexWrap="wrap">
                            <Text fontWeight="medium">{c.title}</Text>
                            <Text color="smMuted">{c.internalParties.map((p) => p.name).join(", ")}</Text>
                            <Text color="smMuted">{ifrsTerm(c)}</Text>
                          </Flex>
                        </Checkbox>
                      ))}
                    </Stack>
                  </Box>
                  {(preview.currentData?.missingIfrsValues.length ?? 0) > 0 && (
                    <Stack spacing="1">
                      <Text fontSize="sm">
                        {t("{{count}} contracts are missing IFRS values and are left out:", {
                          count: preview.currentData?.missingIfrsValues.length,
                        })}
                      </Text>
                      <List spacing="0.5" maxH="24" overflowY="auto" fontSize="sm" color="smMuted">
                        {preview.currentData?.missingIfrsValues.map((c) => (
                          <ListItem key={c.id}>{c.title}</ListItem>
                        ))}
                      </List>
                    </Stack>
                  )}
                  {(preview.currentData?.outsidePeriodCount ?? 0) > 0 && (
                    <Text fontSize="sm" color="smMuted">
                      {t("{{count}} contracts have no months in the period.", {
                        count: preview.currentData?.outsidePeriodCount,
                      })}
                    </Text>
                  )}
                  <Text fontSize="sm">
                    {t("The report includes {{count}} contracts, {{from}}–{{to}}.", {
                      count: chosen.length,
                      from: period.from,
                      to: period.to,
                    })}
                  </Text>
                </Stack>
              )
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap="2" flexWrap="wrap">
          {!preview.isFetching && periodIsValid && chosen.length === 0 && (
            <Text fontSize="sm" color="smMuted" mr="auto">
              {t("No contracts selected")}
            </Text>
          )}
          <Button variant="ghost" onClick={onClose} isDisabled={isDownloading}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" onClick={download} isLoading={isDownloading} isDisabled={blocked}>
            {t("Download")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

